import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { EmptyState, Modal, Panel, StatusChip, ToolbarButton } from '../../../ui-kit';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchWaConversation, openWaFile, sendWaAgain, type WaDetail,
} from '../../../services/websiteAssistantService';
import { useLive, when } from '../rocco/live';
import { intentOf, stateChip } from './status';

/**
 * One chat, start to finish: every line both ways, what went on the lead and
 * where each detail came from (typed, or read from a document), the documents
 * themselves, and what became of it in AgencyZoom. Watches the chat while
 * open, so a visitor still typing appears in place.
 */
export default function ChatStoryModal({ chatId, onClose }: { chatId: string; onClose: () => void }) {
  const canSend = useCan('manage_agents');
  const fetcher = React.useCallback(() => fetchWaConversation(chatId), [chatId]);
  const { data, error, reload } = useLive<WaDetail>(fetcher, 5000);
  const [sending, setSending] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const sendAgain = async () => {
    setSending(true);
    setProblem(null);
    try {
      await sendWaAgain(chatId);
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send it to AgencyZoom');
    } finally {
      setSending(false);
    }
  };

  const outcome = (c: WaDetail): string | null => {
    if (c.source === 'seed') return 'An example: it was never sent to AgencyZoom.';
    if (c.needs_sending_again && c.state !== 'lead_failed') return c.error;
    if (c.state === 'lead_sent') {
      return `In AgencyZoom as lead ${c.lead_id}${c.assigned_to ? `, assigned to ${c.assigned_to}` : ''}`
        + `${c.files ? ', with its documents attached' : ''}.`;
    }
    if (c.state === 'lead_failed') return `Not sent to AgencyZoom: ${c.error}`;
    if (c.state === 'handed') {
      return `Passed to the team: ${c.handed_reason}.${c.task_id
        ? ` A call-back task for ${c.assigned_to || 'the team'} is in AgencyZoom (${c.task_id}).` : ''}`;
    }
    if (c.state === 'answered') return `Answered from ${c.answer_source || 'the policy record'}.`;
    return null;
  };

  return (
    <Modal open onClose={onClose} size="lg"
      title={data ? (data.visitor || 'A visitor') : 'Chat'}
      subtitle={data ? `${intentOf(data.intent)} · started ${when(data.started_at)}` : undefined}
      actions={data ? <StatusChip {...stateChip(data.state)} /> : null}>
      {error && !data ? <EmptyState message={error} /> : null}
      {data ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' }, alignItems: 'start' }}>
          <Panel title="The chat" subtitle="Every line, as the visitor saw it">
            <Stack spacing={1}>
              {data.messages.map((m) => (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: m.role === 'visitor' ? 'flex-end' : 'flex-start' }}>
                  <Box sx={(t) => ({
                    maxWidth: '85%', px: 1.5, py: 1, borderRadius: `${t.ds.radius.md}px`, whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    bgcolor: m.role === 'visitor' ? t.tokens.brand.primarySoft : t.tokens.surface.sunken,
                    color: t.tokens.text.primary,
                  })}>
                    {m.file ? (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <DescriptionOutlined fontSize="small" />
                        <Typography variant="body2">{m.file}</Typography>
                      </Stack>
                    ) : <Typography variant="body2">{m.text}</Typography>}
                    <Typography variant="caption" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary, mt: 0.25 })}>
                      {when(m.at)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Panel>

          <Stack spacing={2}>
            {outcome(data) ? (
              <Panel title="What became of it">
                <Stack spacing={1.25}>
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{outcome(data)}</Typography>
                  {data.needs_sending_again && canSend ? (
                    <Box>
                      <ToolbarButton variant="brand" onClick={sendAgain} loading={sending}>Send to AgencyZoom again</ToolbarButton>
                    </Box>
                  ) : null}
                  {problem ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography> : null}
                </Stack>
              </Panel>
            ) : null}

            {data.details.length ? (
              <Panel title={data.intent === 'quote' ? 'On the lead' : 'What we know'}
                subtitle="Each detail with where it came from">
                <Stack spacing={1}>
                  {data.details.map((d) => (
                    <Box key={d.label} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', columnGap: 1.5 }}>
                      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{d.label}</Typography>
                      <Stack spacing={0}>
                        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
                          {d.value}
                        </Typography>
                        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                          {d.from === 'typed' ? 'Typed in the chat' : `From the ${d.from.charAt(0).toLowerCase()}${d.from.slice(1)}`}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Panel>
            ) : null}

            {data.client?.policy_num ? (
              <Panel title="The client" subtitle="Found by policy number and ZIP code">
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
                  {data.client.name} · {data.client.line} · {data.client.policy_num}
                </Typography>
                {data.question ? (
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, mt: 1 })}>
                    Asked: {data.question}
                  </Typography>
                ) : null}
              </Panel>
            ) : null}

            {data.documents.length ? (
              <Panel title="Documents" subtitle="What the visitor sent, read in the chat">
                <Stack spacing={0.75}>
                  {data.documents.map((d) => (
                    <Stack key={d.id} direction="row" spacing={1} alignItems="center">
                      <DescriptionOutlined fontSize="small" sx={(t) => ({ color: t.tokens.text.tertiary })} />
                      <Box component="button" type="button" onClick={() => void openWaFile(d.id)}
                        sx={(t) => ({ all: 'unset', cursor: 'pointer', color: t.tokens.brand.primary,
                          typography: 'body2', '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing } })}>
                        {d.name}
                      </Box>
                      <Typography variant="caption" sx={(t) => ({ color: d.error ? t.tokens.semantic.danger : t.tokens.text.tertiary })}>
                        {d.error ? 'Could not be read' : d.kind || ''}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Panel>
            ) : null}
          </Stack>
        </Box>
      ) : null}
    </Modal>
  );
}
